import { useState, useMemo } from 'react';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  Box,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  TextField,
  InputAdornment,
  Slider,
  Paper,
} from '@mui/material';
import { AutoFixHigh as GenerateIcon, Search as SearchIcon } from '@mui/icons-material';

interface ValidatedKeywordEntry {
  keyword: string;
  relevanceScore?: number | null;
  reason?: string | null;
  selectedByUser?: boolean | null;
  userId?: number | null;
  selectionTimestamp?: string | null;
}

interface ValidatedKeyword {
  dbId?: number | null;
  companyId?: number | null;
  companyDomain?: string | null;
  companyName?: string | null;
  userId?: number | null;
  isSoftware?: boolean | null;
  validationTimestamp?: string | null;
  validatedKeywords?: ValidatedKeywordEntry[] | null;
}

interface KeywordAggregation {
  keyword: string;
  usageCount: number;
  softwareCount: number;
  nonSoftwareCount: number;
  avgRelevanceScore: number;
  examples: ValidatedKeyword[];
}

interface ValidatedKeywordsDataGridProps {
  keywords: ValidatedKeyword[];
  onGeneratePattern: (keyword: string, classificationType: 'software' | 'non_software') => Promise<void>;
  loading?: boolean;
}

const ValidatedKeywordsDataGrid = ({
  keywords,
  onGeneratePattern,
  loading = false,
}: ValidatedKeywordsDataGridProps) => {
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [keywordToGenerate, setKeywordToGenerate] = useState<string | null>(null);
  const [generateLoading, setGenerateLoading] = useState(false);
  const [classificationType, setClassificationType] = useState<'software' | 'non_software'>('non_software');
  const [searchText, setSearchText] = useState('');
  const [minUsageCount, setMinUsageCount] = useState(2);
  const [convertedKeywords, setConvertedKeywords] = useState<Set<string>>(new Set());
  const [hideConverted, setHideConverted] = useState(true);

  // Aggregate keywords across all validations
  const aggregatedKeywords = useMemo(() => {
    const keywordMap = new Map<string, KeywordAggregation>();

    keywords.forEach(validation => {
      if (!validation.validatedKeywords) return;

      validation.validatedKeywords.forEach(kw => {
        if (!kw.keyword) return;

        const keyword = kw.keyword.toLowerCase();
        const existing = keywordMap.get(keyword);

        if (existing) {
          existing.usageCount += 1;
          if (validation.isSoftware) {
            existing.softwareCount += 1;
          } else {
            existing.nonSoftwareCount += 1;
          }
          existing.avgRelevanceScore =
            (existing.avgRelevanceScore * (existing.usageCount - 1) + (kw.relevanceScore || 0)) / existing.usageCount;
          existing.examples.push(validation);
        } else {
          keywordMap.set(keyword, {
            keyword: kw.keyword,
            usageCount: 1,
            softwareCount: validation.isSoftware ? 1 : 0,
            nonSoftwareCount: validation.isSoftware ? 0 : 1,
            avgRelevanceScore: kw.relevanceScore || 0,
            examples: [validation],
          });
        }
      });
    });

    // Filter and sort
    let filtered = Array.from(keywordMap.values());

    // Apply minimum usage count filter
    filtered = filtered.filter(k => k.usageCount >= minUsageCount);

    // Apply search text filter
    if (searchText.trim()) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(k => k.keyword.toLowerCase().includes(searchLower));
    }

    // Apply hide converted filter
    if (hideConverted) {
      filtered = filtered.filter(k => !convertedKeywords.has(k.keyword.toLowerCase()));
    }

    // Sort by usage count (descending)
    return filtered.sort((a, b) => b.usageCount - a.usageCount);
  }, [keywords, searchText, minUsageCount, hideConverted, convertedKeywords]);

  const handleGenerateClick = (keyword: string) => {
    setKeywordToGenerate(keyword);

    // Auto-detect classification type based on usage statistics
    const keywordData = aggregatedKeywords.find(k => k.keyword === keyword);
    if (keywordData) {
      const defaultType = keywordData.softwareCount > keywordData.nonSoftwareCount ? 'software' : 'non_software';
      setClassificationType(defaultType);
    }

    setGenerateDialogOpen(true);
  };

  const handleGenerateConfirm = async () => {
    if (!keywordToGenerate) return;
    setGenerateLoading(true);
    try {
      await onGeneratePattern(keywordToGenerate, classificationType);

      // Mark keyword as converted
      setConvertedKeywords(prev => new Set(prev).add(keywordToGenerate.toLowerCase()));

      setGenerateDialogOpen(false);
      setKeywordToGenerate(null);
      setClassificationType('non_software'); // Reset to default
    } finally {
      setGenerateLoading(false);
    }
  };

  const handleGenerateCancel = () => {
    setGenerateDialogOpen(false);
    setKeywordToGenerate(null);
    setClassificationType('non_software'); // Reset to default
  };

  const columns: GridColDef[] = [
    {
      field: 'keyword',
      headerName: 'Keyword',
      flex: 2,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams<KeywordAggregation>) => (
        <Tooltip title={`Used in ${params.row.usageCount} validation(s)`}>
          <Box
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              fontWeight: 500,
            }}
          >
            {params.value}
          </Box>
        </Tooltip>
      ),
    },
    {
      field: 'usageCount',
      headerName: 'Usage Count',
      width: 120,
      type: 'number',
      renderCell: (params: GridRenderCellParams<KeywordAggregation>) => (
        <Chip label={params.value} size="small" color="primary" variant="outlined" />
      ),
    },
    {
      field: 'classification',
      headerName: 'Classification',
      width: 180,
      renderCell: (params: GridRenderCellParams<KeywordAggregation>) => {
        const softwareCount = params.row.softwareCount;
        const nonSoftwareCount = params.row.nonSoftwareCount;
        const total = softwareCount + nonSoftwareCount;

        return (
          <Box display="flex" gap={1} alignItems="center">
            <Tooltip title={`${softwareCount} software classifications`}>
              <Chip label={`SW: ${softwareCount}`} size="small" color="success" />
            </Tooltip>
            <Tooltip title={`${nonSoftwareCount} non-software classifications`}>
              <Chip label={`Non-SW: ${nonSoftwareCount}`} size="small" color="warning" />
            </Tooltip>
          </Box>
        );
      },
    },
    {
      field: 'avgRelevanceScore',
      headerName: 'Avg Relevance',
      width: 130,
      type: 'number',
      renderCell: (params: GridRenderCellParams<KeywordAggregation>) => (
        <Box sx={{ fontWeight: 600 }}>{params.value ? (params.value * 100).toFixed(0) + '%' : 'N/A'}</Box>
      ),
    },
    {
      field: 'examples',
      headerName: 'Recent Companies',
      flex: 2,
      minWidth: 250,
      renderCell: (params: GridRenderCellParams<KeywordAggregation>) => {
        const recentExamples = params.value.slice(0, 3);
        return (
          <Box display="flex" gap={0.5} flexWrap="wrap">
            {recentExamples.map((example: ValidatedKeyword, idx: number) => (
              <Chip
                key={idx}
                label={example.companyName || example.companyDomain || 'Unknown'}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.7rem' }}
              />
            ))}
            {params.value.length > 3 && (
              <Chip
                label={`+${params.value.length - 3} more`}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.7rem' }}
              />
            )}
          </Box>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      renderCell: (params: GridRenderCellParams<KeywordAggregation>) => {
        const isConverted = convertedKeywords.has(params.row.keyword.toLowerCase());

        return (
          <Tooltip
            title={
              isConverted
                ? 'Already converted to pattern (click to generate another)'
                : 'Generate classification pattern from this keyword (takes 10-30 seconds)'
            }
          >
            <IconButton
              size="small"
              onClick={() => handleGenerateClick(params.row.keyword)}
              color={isConverted ? 'success' : 'primary'}
              sx={isConverted ? { opacity: 0.6 } : undefined}
            >
              <GenerateIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <>
      {/* Filter Controls */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" gap={2} alignItems="flex-start" flexWrap="wrap">
          <TextField
            size="small"
            placeholder="Search keywords..."
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 250, flex: '1 1 auto' }}
          />
          <Box sx={{ minWidth: 300, flex: '1 1 auto' }}>
            <Typography variant="caption" color="text.secondary" gutterBottom>
              Minimum Usage Count: {minUsageCount}
            </Typography>
            <Slider
              value={minUsageCount}
              onChange={(_, value) => setMinUsageCount(value as number)}
              min={1}
              max={20}
              marks={[
                { value: 1, label: '1' },
                { value: 5, label: '5' },
                { value: 10, label: '10' },
                { value: 20, label: '20+' },
              ]}
              valueLabelDisplay="auto"
              size="small"
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip label={`${aggregatedKeywords.length} keywords`} color="primary" variant="outlined" size="small" />
            <Tooltip title={hideConverted ? 'Click to show already converted keywords' : 'Hide converted keywords'}>
              <Chip
                label={hideConverted ? 'Converted hidden' : 'Showing all'}
                color={hideConverted ? 'success' : 'default'}
                variant={hideConverted ? 'filled' : 'outlined'}
                size="small"
                onClick={() => setHideConverted(!hideConverted)}
                sx={{ cursor: 'pointer' }}
              />
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      <DataGrid
        rows={aggregatedKeywords}
        columns={columns}
        getRowId={row => row.keyword}
        loading={loading}
        pageSizeOptions={[25, 50, 100]}
        initialState={{
          pagination: { paginationModel: { pageSize: 25 } },
          sorting: { sortModel: [{ field: 'usageCount', sort: 'desc' }] },
        }}
        sx={{
          height: '100%',
          '& .MuiDataGrid-cell': {
            borderBottom: '1px solid rgba(224, 224, 224, 0.5)',
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.02)',
          },
        }}
        disableRowSelectionOnClick
      />

      <Dialog open={generateDialogOpen} onClose={handleGenerateCancel}>
        <DialogTitle>Generate Classification Pattern</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Generate a new classification pattern from the keyword <strong>"{keywordToGenerate}"</strong>?
          </DialogContentText>
          {keywordToGenerate && aggregatedKeywords.find(k => k.keyword === keywordToGenerate) && (
            <Box mt={2}>
              <Typography variant="body2" color="text.secondary">
                Based on {aggregatedKeywords.find(k => k.keyword === keywordToGenerate)?.usageCount} user selections:
              </Typography>
              <Box mt={1} display="flex" gap={1}>
                <Chip
                  label={`${aggregatedKeywords.find(k => k.keyword === keywordToGenerate)?.softwareCount} Software`}
                  size="small"
                  color="success"
                />
                <Chip
                  label={`${aggregatedKeywords.find(k => k.keyword === keywordToGenerate)?.nonSoftwareCount} Non-Software`}
                  size="small"
                  color="warning"
                />
              </Box>
            </Box>
          )}
          <Box mt={3}>
            <FormControl component="fieldset">
              <FormLabel component="legend">Pattern Type</FormLabel>
              <RadioGroup
                value={classificationType}
                onChange={e => setClassificationType(e.target.value as 'software' | 'non_software')}
              >
                <FormControlLabel
                  value="software"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        Software Indicator
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        This keyword indicates the company IS software (e.g., "project management software", "SaaS
                        platform")
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="non_software"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        Non-Software Indicator
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        This keyword indicates the company is NOT software (e.g., "IT consulting", "reseller",
                        "services")
                      </Typography>
                    </Box>
                  }
                />
              </RadioGroup>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleGenerateCancel} disabled={generateLoading}>
            Cancel
          </Button>
          <Button onClick={handleGenerateConfirm} color="primary" disabled={generateLoading} variant="contained">
            {generateLoading ? 'Generating (10-30s)...' : 'Generate Pattern'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ValidatedKeywordsDataGrid;
